import { DiscoveryLevel, VanityType } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { editVanityRedirectKeyForTopic, getRedirectKeyAvailable } from '../../../helpers/api/topics-api';

import { HOST } from '../../../helpers/constants';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import { dispatchSettingsUpdate } from '../../../helpers/settings';
import { getRedirectKeyFromUrl } from '../../../helpers/urls';
import topicData from '../../../helpers/state/topic-data';
import { getMainPage } from '../../../helpers/page-name';

@customElement('custom-urls')
export class CustomUrls extends LitElement {
    amplitude_callsite = 'Custom Urls';



    @state()
    availableRedirects: VanityType[] = [];

	private readonly bindTopicState = new StateController(this, topicData);

    firstUpdated() {
        const availableRedirects = [];
        if (topicData.discoveryLevel === DiscoveryLevel.Private) {
            availableRedirects.push(VanityType.Read);
        }

        if (topicData.discoveryLevel === DiscoveryLevel.Private || topicData.discoveryLevel === DiscoveryLevel.ReadPublic) {
            availableRedirects.push(VanityType.Addpoint);
        }

        if (topicData.discoveryLevel === DiscoveryLevel.ReadPublic || topicData.discoveryLevel === DiscoveryLevel.AddpointPublic) {
            availableRedirects.push(VanityType.Keyless);
        }

        this.availableRedirects = availableRedirects; // set to pre-existing array because just pushing to this.availableRedirects doesn't trigger a re-render
    }

    async updateVanityUrl(newUrl: string) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await editVanityRedirectKeyForTopic(
                this.amplitude_callsite,
                topicData.topicId,
                newUrl,
                topicData.topicKey
            );
        }, 'An error has occurred.  Context: the server rejected the addpoint vanity redirect key request due to validation issues.');
    }

	async onChangeUrl(newUrl: string) {
        const urlAvailable = await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const isAvailable = await getRedirectKeyAvailable(
                this.amplitude_callsite,
                newUrl
            );

            return isAvailable;
        }, 'Error checking if redirect is available');

        if (!urlAvailable) {
            getMainPage().errorToastElement.showToastAndThrow('That Shorthand Link is already taken or is invalid. Please try another one!');
        }

        dispatchSettingsUpdate(
            {
				updatedValue: topicData.name, // we are just sending the name back because this data doesnt update topic, just has a callback
				field: 'name', // this is a hacky way of getting around the fact that this setting breaks the pattern because it doesnt update the topic
                callback: this.updateVanityUrl.bind(this, newUrl),
            },
            this
        );
	}

    async onChangeSocialMediaImage(e: Event) {
		const url = (e.target as HTMLInputElement).value;

        dispatchSettingsUpdate(
            {
                updatedValue: url,
                field: 'custom_social_media_image',
            },
            this
        );
	}

    render() {
        return html`
            <h4 class="sub-title">Custom Map Short-links</h4>
            <p class="info-paragraph">
                Create a unique link for your map. Updating this will not break existing links.
            </p>
            <proxi-textfield
                class="input"
                prefix="${HOST}/r/"
                outlined
                pattern="^[A-Za-z0-9_-]*$"
                validationMessage="Only letters, numbers, underscores, and dashes are allowed; in addition, it must be at least 6 characters.  Otherwise, it may have already been taken: please try again."
                charcounter
                maxlength="50"
                @change="${async (e: any) => { await this.onChangeUrl(e.target.value); }}"
                value=${getRedirectKeyFromUrl(topicData.topic?.cc_read_link)}>
            </proxi-textfield>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .input {
                width: 100%;
                padding: auto 5px;
            }

            .info-paragraph {
                font-size: 0.8rem;
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 10px;
            }

            .sub-title {
                margin-bottom: 5px;
                margin-top: 5px;
                color: var(--theme-input-color);
            }

            .helpSubHeader {
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
            }
        `;
    }
}
